class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  processingUpgrade = false; // Add processingUpgrade flag here

  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname.includes("cart");
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);

    // Initial event listener setup (will be re-bound in renderContents)
    this.setupUpgradeButtonListener();
  }

  setupUpgradeButtonListener() {
    this.removeEventListener('click', this.upgradeButtonClickHandler); // Remove any existing listener
    this.addEventListener('click', this.upgradeButtonClickHandler); // Add the listener
  }

  upgradeButtonClickHandler = (event) => {
    if (event.target.classList.contains('wt-cart__upgrade-button-10469341462859')) {
      event.preventDefault();
      const button = event.target;
      const itemKey = button.dataset.itemKey;

      if (this.processingUpgrade || !itemKey) return;
      this.processingUpgrade = true;

      button.textContent = '...';
      button.disabled = true;

      this.upgradeToOptimalQuantity(itemKey, button);
    }
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) return;
    this.init();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
        this.closeButton().focus();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      this.mainTrigger.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.closeButton().focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });
  }

  renderContents(parsedState, isClosedCart=true) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      
      if (sectionElement) {
        sectionElement.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector,
        );
      }
    });

    // Re-bind event listener for upgrade button after content is rendered
    this.setupUpgradeButtonListener();
    
    if(isClosedCart) {
      setTimeout(() => {
        this.toggleDrawerClasses();
        if (this.isOpen) {
          this.closeButton().focus();
        }
      });
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  upgradeToOptimalQuantity(itemKey, buttonElement) {
    console.log("upgradeToOptimalQuantity called with itemKey:", itemKey);
    const newQuantity = 4;
    
    // Extract variant ID from itemKey for fallback (assuming format is "variantId:uniqueId")
    const variantId = itemKey.split(':')[0];
    console.log("Extracted variantId:", variantId);
    
    // Get the current locale
    const locale = document.documentElement.lang || 'en';
    
    // Translations for different states
    const translations = {
      updating: {
        'pl': '...',
        'en': '...',
        'de': '...'
      },
      upgraded: {
        'pl': 'Ulepszono!',
        'en': 'Upgraded!',
        'de': 'Upgraded!'
      },
      upgrade: {
        'pl': 'Ulepsz ofertę',
        'en': 'Upgrade offer',
        'de': 'Angebot upgraden'
      }
    };
    
    // Get the correct translation based on locale
    const getTranslation = (key) => {
      return translations[key][locale] || translations[key]['en']; // Fallback to English
    };
    
    // Add fading-out class to the button
    buttonElement.classList.add('fading-out');
    
    // After fade-out transition, proceed with cart update
    setTimeout(() => {
      buttonElement.textContent = getTranslation('updating'); // Change text after fade out
      buttonElement.disabled = true;
      
      // Try updating with the item key first
      fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: newQuantity
        })
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(cart => {
        console.log("cart/change.js response:", cart);
        this.fetchCartAndUpdateDrawer();
        
        // On success: change button to success state for 1 sec, then reset
        buttonElement.textContent = getTranslation('upgraded');
        buttonElement.classList.add('success'); // Add success class for green style
        
        setTimeout(() => {
          buttonElement.classList.remove('success'); // Remove success class
          buttonElement.textContent = getTranslation('upgrade'); // Reset text to initial state
          buttonElement.disabled = false;
          this.processingUpgrade = false;
        }, 1000); // 1 second success display
      })
      .catch(error => {
        console.error('Error updating cart with item key, trying variant ID:', error);
        
        // Fallback: try with variant ID instead
        fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: variantId,
            quantity: newQuantity
          })
        })
        .then(response => response.json())
        .then(cart => {
          console.log("Fallback cart update successful");
          this.fetchCartAndUpdateDrawer();
          
          buttonElement.textContent = getTranslation('upgraded');
          buttonElement.classList.add('success');
          
          setTimeout(() => {
            buttonElement.classList.remove('success'); // Remove success class
            buttonElement.textContent = getTranslation('upgrade'); // Reset text to initial state
            buttonElement.disabled = false;
            this.processingUpgrade = false;
          }, 1000);
        })
        .catch(finalError => {
          console.error('Final error updating cart:', finalError);
          buttonElement.classList.remove('fading-out');
          buttonElement.textContent = getTranslation('upgrade');
          buttonElement.disabled = false;
          this.processingUpgrade = false;
        });
      });
    }, 300); // Duration should match the CSS transition (0.3s)
  }

  fetchCartAndUpdateDrawer() {
    fetch('?sections=cart-drawer,cart-icon-bubble')
      .then(response => response.json())
      .then(sections => {
        this.renderContents(
          { sections: sections },
          false
        );
      })
      .catch(error => {
        console.error('Error fetching cart sections:', error);
      });
  }
}

customElements.define("cart-drawer", CartDrawerSection);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

// Add cart drawer currency update when currency changes
document.addEventListener('DOMContentLoaded', function() {
  // Listen for currency changes in Shopify
  if (typeof Shopify !== 'undefined' && Shopify.currency) {
    const originalSelectCallback = window.selectCallback || function(){};
    
    window.selectCallback = function() {
      originalSelectCallback.apply(this, arguments);
      
      // Update cart currency display
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.updateCartCurrencyDisplay === 'function') {
        cartDrawer.updateCartCurrencyDisplay();
      }
    };
  }
});